/**
 * Shared template form + channel chip — used by the per-study TemplatesTab and
 * the network-wide GlobalTemplates admin page so both edit templates the same way.
 */
import { useRef, useState } from 'react';
import { MessageSquare, Mail, Layers, Hand, FileText, Tag, AlertTriangle } from 'lucide-react';
import VariablePicker from '../VariablePicker';
import { findUnknownMergeFields, insertAtCursor, MERGE_FIELDS } from '../../mergeFields';

export type TemplateChannel = 'sms' | 'email' | 'any';
export type TemplateCategory = 'welcome' | 'study_details' | 'general';
export interface TemplateDraft { channel: TemplateChannel; category: TemplateCategory; title: string; subject: string; body: string }

const CHANNEL_META: Record<TemplateChannel, { label: string; icon: typeof MessageSquare }> = {
  sms: { label: 'Text', icon: MessageSquare },
  email: { label: 'Email', icon: Mail },
  any: { label: 'Text or email', icon: Layers },
};

export const CATEGORY_META: Record<TemplateCategory, { label: string; icon: typeof MessageSquare }> = {
  welcome: { label: 'Welcome', icon: Hand },
  study_details: { label: 'Study details', icon: FileText },
  general: { label: 'General', icon: Tag },
};

export function ChannelChip({ channel }: { channel: TemplateChannel }) {
  const meta = CHANNEL_META[channel] || CHANNEL_META.any;
  const Icon = meta.icon;
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
      <Icon className="h-3 w-3" /> {meta.label}
    </span>
  );
}

export function CategoryChip({ category }: { category: TemplateCategory }) {
  const meta = CATEGORY_META[category] || CATEGORY_META.general;
  const Icon = meta.icon;
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">
      <Icon className="h-3 w-3" /> {meta.label}
    </span>
  );
}

/** Inline create/edit form for one template. */
export default function TemplateForm({ initial, busy, onSave, onCancel }: {
  initial?: Partial<TemplateDraft>;
  busy: boolean;
  onSave: (v: TemplateDraft) => void;
  onCancel: () => void;
}) {
  const [channel, setChannel] = useState<TemplateChannel>((initial?.channel as TemplateChannel) || 'sms');
  const [category, setCategory] = useState<TemplateCategory>((initial?.category as TemplateCategory) || 'general');
  const [title, setTitle] = useState(initial?.title || '');
  const [subject, setSubject] = useState(initial?.subject || '');
  const [body, setBody] = useState(initial?.body || '');
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const subjectRef = useRef<HTMLInputElement | null>(null);
  // Insert a clicked token into whichever field the recruiter last focused.
  const [activeField, setActiveField] = useState<'body' | 'subject'>('body');

  // Mistyped tokens ({{study}}) would send blank — block the save and explain.
  const uniqUnknown = [
    ...new Set([
      ...findUnknownMergeFields(body),
      ...(channel === 'email' ? findUnknownMergeFields(subject) : []),
    ]),
  ];
  const valid = title.trim() && body.trim() && uniqUnknown.length === 0;

  const insertToken = (token: string) => {
    if (activeField === 'subject' && channel === 'email') {
      insertAtCursor(subjectRef.current, subject, setSubject, token);
    } else {
      insertAtCursor(bodyRef.current, body, setBody, token);
    }
  };

  return (
    <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/40">
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={channel}
          onChange={(e) => setChannel(e.target.value as TemplateChannel)}
          aria-label="Channel"
          className="rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
        >
          <option value="sms">Text only</option>
          <option value="email">Email only</option>
          <option value="any">Text or email</option>
        </select>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value as TemplateCategory)}
          aria-label="Category"
          title="Which tab this template appears under in the composer picker"
          className="rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
        >
          <option value="welcome">Welcome text</option>
          <option value="study_details">Study details</option>
          <option value="general">General</option>
        </select>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={120}
          placeholder="Label (e.g. First text — intro)"
          aria-label="Template label"
          className="min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
        />
      </div>
      {category === 'welcome' && (
        <p className="text-[11px] text-gray-400">
          A reply recruiters send from the composer — not the automatic “New Lead Welcome” first text (that lives in Flows).
        </p>
      )}
      {channel === 'email' && (
        <input
          ref={subjectRef}
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          onFocus={() => setActiveField('subject')}
          maxLength={200}
          placeholder="Email subject (optional)"
          aria-label="Email subject"
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
        />
      )}
      <textarea
        ref={bodyRef}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        onFocus={() => setActiveField('body')}
        rows={3}
        maxLength={4000}
        placeholder="Message… you can use {{first_name}} and {{study_title}}"
        aria-label="Template body"
        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
      />
      <VariablePicker items={MERGE_FIELDS} onInsert={insertToken} />
      {uniqUnknown.length > 0 && (
        <p className="flex items-start gap-1 text-xs text-amber-600 dark:text-amber-400">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            Unknown field{uniqUnknown.length > 1 ? 's' : ''}: {uniqUnknown.map((t) => `{{${t}}}`).join(', ')} — these would send blank. Use the Insert field buttons above.
          </span>
        </p>
      )}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => valid && onSave({ channel, category, title: title.trim(), subject: subject.trim(), body: body.trim() })}
          disabled={!valid || busy}
          className="rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}
